import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  FaUserGraduate,
  FaUser,
  FaUserTie,
  FaBook,
  FaSchool,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaCreditCard,
  FaPercent,
  FaCalculator,
  FaSearch,
  FaEye,
  FaPaperPlane,
  FaFileInvoiceDollar,
  FaHistory,
  FaSync,
  FaArrowLeft,
  FaArrowRight,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes,
  FaPhone,
  FaMapMarkerAlt,
  FaVenusMars,
  FaUsers,
  FaIdCard,
  FaChartLine,
  FaFilter,
  FaEdit,
  FaDatabase,
  FaBug,
  FaSpinner,
  FaBan,
} from "react-icons/fa";

import "./ApplySoloStdFee.css";

// API base URL
const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api/ManageFeeAndExpense.php";

// Helper function for API calls with retry mechanism
const callAPI = async (action, data = null, method = "POST", retries = 2) => {
  let lastError = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      let url = `${API_BASE_URL}?action=${action}`;

      let config = {
        method: method,
        url: url,
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 30000,
        validateStatus: function (status) {
          // Accept all status codes - we'll handle them manually
          return true;
        },
      };

      if (method.toUpperCase() === "GET" && data) {
        const params = new URLSearchParams();
        Object.keys(data).forEach((key) => {
          if (data[key] !== null && data[key] !== undefined) {
            params.append(key, data[key]);
          }
        });
        url = `${url}&${params.toString()}`;
        config.url = url;
      } else if (data) {
        config.data = data;
      }

      console.log(
        `API Call (Attempt ${attempt + 1}/${retries + 1}): ${method} ${url}`,
        data
      );

      const response = await axios(config);
      console.log(`API Response for ${action}:`, response.data, `Status: ${response.status}`);

      // For any non-200 response, add error flags
      if (response.status !== 200) {
        return {
          ...response.data,
          _httpStatus: response.status,
          _isHttpError: true,
        };
      }

      return response.data;
    } catch (error) {
      lastError = error;

      if (attempt < retries) {
        console.warn(
          `Attempt ${attempt + 1} failed, retrying in ${
            (attempt + 1) * 1000
          }ms...`
        );
        await new Promise((resolve) =>
          setTimeout(resolve, (attempt + 1) * 1000)
        );
        continue;
      }

      console.error(`API Error for action ${action}:`, error);

      if (error.response) {
        // Server responded with error status
        return {
          error:
            error.response.data?.error ||
            `Server Error: ${error.response.status}`,
          _httpStatus: error.response.status,
          _isHttpError: true,
          ...error.response.data,
        };
      } else if (error.request) {
        throw {
          message:
            "No response from server. Please check your network connection.",
          status: 0,
          isNetworkError: true,
        };
      } else {
        throw {
          message: error.message || "Unknown error occurred",
          status: -1,
        };
      }
    }
  }
};

const ApplySoloStdFee = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const className = location.state?.className || "Play Group";
  const inputRefs = useRef([]);

  // State for Student ID fields
  const [studentIdPart2, setStudentIdPart2] = useState("");
  const [studentIdPart3, setStudentIdPart3] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Family Student State
  const [isFamilyStudent, setIsFamilyStudent] = useState(false);
  const [familyStudentData, setFamilyStudentData] = useState(null);

  // Months Setting - Fixed initial dates
  const currentDate = new Date();
  const [startMonth, setStartMonth] = useState(() => {
    // Set to first day of current month
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  });
  const [endMonth, setEndMonth] = useState(() => {
    // Set to first day of month 11 months from now (12 months total)
    const date = new Date(currentDate);
    date.setMonth(date.getMonth() + 11);
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  // Fee Calculations
  const [totalFee, setTotalFee] = useState(0);
  const [totalPaid, setTotalPaid] = useState("");
  const [lastPending, setLastPending] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [monthCount, setMonthCount] = useState(0);

  // Existing Records and Update Mode
  const [existingMonthlyFee, setExistingMonthlyFee] = useState(null);
  const [existingExpense, setExistingExpense] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [existingRecords, setExistingRecords] = useState(null);

  // Total Record State
  const [existingTotalRecord, setExistingTotalRecord] = useState(null);
  const [isTotalRecordUpdateMode, setIsTotalRecordUpdateMode] = useState(false);

  // Student Data
  const [studentData, setStudentData] = useState({
    studentId: "",
    studentName: "",
    fatherName: "",
    studentClass: "",
    annualCharges: 0,
    admissionFee: 0,
    workbookCharges: 0,
    booksCharges: 0,
    monthlyFee: 0,
    totalPayable: 0,
    discount: "0",
    finalTotal: 0,
    mobile: "",
    bFormNo: "",
    address: "",
    gender: "",
    section: "",
    family_status: "",
    admissionDate: "",
    discountPercentage: 0,
  });

  // Fee History
  const [feeHistory, setFeeHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // System State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);
  const [systemStatus, setSystemStatus] = useState("ready");

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current.length > 0) {
      inputRefs.current[0]?.focus();
    }
  }, []);

  // Load student ID from URL params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const storedId = searchParams.get("studentId");
    if (storedId) {
      const parts = storedId.split("-");
      if (parts.length === 3) {
        setStudentIdPart2(parts[1]);
        setStudentIdPart3(parts[2]);
      }
    }
  }, [location]);

  // Fetch fee history when student data is loaded
  useEffect(() => {
    if (studentData.studentId && !isFamilyStudent) {
      fetchFeeHistory(studentData.studentId);
    }
  }, [studentData.studentId, isFamilyStudent]);

  // Check for existing records when student and month change
  useEffect(() => {
    if (studentData.studentId && startMonth && !isFamilyStudent) {
      checkExistingRecords();
    }
  }, [studentData.studentId, startMonth, isFamilyStudent]);

  // Calculate total fee when dates or monthly fee changes
  useEffect(() => {
    if (studentData.studentId && !isFamilyStudent) {
      calculateTotalFee();
    }
  }, [startMonth, endMonth, studentData.monthlyFee]);

  // Calculate final total when fee components change
  useEffect(() => {
    if (studentData.studentId && !isFamilyStudent) {
      calculateFinalTotal();
    }
  }, [
    studentData.totalPayable,
    studentData.discountPercentage,
    totalPaid,
  ]);

  // Add API log
  const addApiLog = useCallback((log) => {
    setApiLogs((prev) => [
      {
        timestamp: new Date().toISOString(),
        ...log,
      },
      ...prev.slice(0, 50),
    ]);
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    return `Rs. ${parseFloat(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

  // Helper function to calculate appropriate start month
  const calculateStartMonth = useCallback((admissionDateStr) => {
    const today = new Date();

    if (!admissionDateStr) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }

    const admissionMoment = moment(admissionDateStr);
    if (!admissionMoment.isValid()) {
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }

    const admissionDate = admissionMoment.toDate();

    // If admission date is in current or future month, use admission date
    // If admission date is in past, use current month
    const currentYearMonth = today.getFullYear() * 12 + today.getMonth();
    const admissionYearMonth =
      admissionDate.getFullYear() * 12 + admissionDate.getMonth();

    if (admissionYearMonth >= currentYearMonth) {
      // Admission is current or future month
      return new Date(admissionDate.getFullYear(), admissionDate.getMonth(), 1);
    } else {
      // Admission was in past, start from current month
      return new Date(today.getFullYear(), today.getMonth(), 1);
    }
  }, []);

  // Show Family Student Modal
  const showFamilyStudentModal = (data) => {
    Swal.fire({
      title: "Family Student Detected",
      html: `
        <div class="family-student-modal">
          <div class="family-student-warning">
            <FaExclamationTriangle style="color: #e74c3c; font-size: 3rem; margin-bottom: 1rem;" />
            <h3>Family Student Detected</h3>
          </div>
          <div class="family-student-message">
            <p><strong>${data.student.name}</strong> belongs to a family.</p>
            <p><strong>Family Status:</strong> ${data.student.family_status}</p>
            ${
              data.family_code
                ? `<p><strong>Family Code:</strong> ${data.family_code}</p>`
                : ""
            }
          </div>
          <div class="family-student-instructions">
            <p><strong>Important:</strong> Family student fees cannot be applied from this screen.</p>
            <p>Please use the <strong>Family Fee Management</strong> section to apply fees for this student.</p>
          </div>
          <div class="family-student-actions">
            <button class="nav-to-family-btn">
              Go to Family Fee Management
            </button>
          </div>
        </div>
      `,
      icon: "info",
      showConfirmButton: false,
      showCloseButton: true,
      width: "600px",
      customClass: {
        popup: "swal-wide family-student-popup",
      },
      didOpen: () => {
        document
          .querySelector(".nav-to-family-btn")
          ?.addEventListener("click", () => {
            navigate("/Fee-ApplyTo-Family-Student");
            Swal.close();
          });
      },
    });
  };

  // Navigate to Family Fee Management
  const handleNavigateToFamilyFee = () => {
    navigate("/Fee-ApplyTo-Family-Student");
  };

  // Calculate total fee based on months - FIXED VERSION
  const calculateTotalFee = useCallback(() => {
    if (!startMonth || !endMonth) {
      setTotalFee(0);
      setMonthCount(0);
      return;
    }

    try {
      const start = moment(startMonth);
      const end = moment(endMonth);
      
      // Calculate the difference in months
      let monthCount = end.diff(start, 'months') + 1; // +1 to include both start and end months
      
      if (monthCount < 0) monthCount = 0;

      setMonthCount(monthCount);

      const monthlyFeeAmount = parseFloat(studentData.monthlyFee) || 0;
      const totalFeeAmount = monthlyFeeAmount * monthCount;

      setTotalFee(totalFeeAmount);
      
      // Update total payable
      const totalOneTimeCharges = 
        (parseFloat(studentData.annualCharges) || 0) +
        (parseFloat(studentData.admissionFee) || 0) +
        (parseFloat(studentData.workbookCharges) || 0) +
        (parseFloat(studentData.booksCharges) || 0);
      
      const totalPayable = totalFeeAmount + totalOneTimeCharges;
      
      setStudentData(prev => ({
        ...prev,
        totalPayable: totalPayable
      }));
      
    } catch (error) {
      console.error("Error calculating total fee:", error);
      setTotalFee(0);
      setMonthCount(0);
    }
  }, [startMonth, endMonth, studentData.monthlyFee, studentData.annualCharges, studentData.admissionFee, studentData.workbookCharges, studentData.booksCharges]);

  // Calculate final total
  const calculateFinalTotal = useCallback(() => {
    try {
      const totalPayable = parseFloat(studentData.totalPayable) || 0;
      const discountPercentage = parseFloat(studentData.discountPercentage) || 0;

      const discountAmount = (totalPayable * discountPercentage) / 100;
      const finalTotalAmount = totalPayable - discountAmount;

      setFinalTotal(finalTotalAmount);
      
      const paid = parseFloat(totalPaid) || 0;
      const pending = finalTotalAmount - paid;
      
      setLastPending(pending < 0 ? 0 : pending);
      
    } catch (error) {
      console.error("Error calculating final total:", error);
      setFinalTotal(0);
      setLastPending(0);
    }
  }, [studentData.totalPayable, studentData.discountPercentage, totalPaid]);

  // Handle total paid change
  const handleTotalPaidChange = useCallback(
    (value) => {
      const paidAmount = parseFloat(value) || 0;
      setTotalPaid(value);

      const totalPayable = parseFloat(studentData.totalPayable) || 0;
      const discountPercentage =
        parseFloat(studentData.discountPercentage) || 0;
      const discountAmount = (totalPayable * discountPercentage) / 100;
      const finalTotalAmount = totalPayable - discountAmount;

      const pending = finalTotalAmount - paidAmount;
      setLastPending(pending < 0 ? 0 : pending);
    },
    [studentData.totalPayable, studentData.discountPercentage]
  );

  // Handle discount change
  const handleDiscountChange = useCallback(
    (value) => {
      let discountValue = parseFloat(value);
      if (isNaN(discountValue) || discountValue < 0) discountValue = 0;
      if (discountValue > 100) discountValue = 100;

      setStudentData((prev) => ({
        ...prev,
        discount: discountValue.toString(),
        discountPercentage: discountValue,
      }));

      const totalPayable = parseFloat(studentData.totalPayable) || 0;
      const discountAmount = (totalPayable * discountValue) / 100;
      const finalTotalAmount = totalPayable - discountAmount;

      setFinalTotal(finalTotalAmount);
      
      const paid = parseFloat(totalPaid) || 0;
      const pending = finalTotalAmount - paid;
      
      setLastPending(pending < 0 ? 0 : pending);
    },
    [studentData.totalPayable, totalPaid]
  );

  // Fetch fee history
  const fetchFeeHistory = async (studentId) => {
    setLoadingHistory(true);
    try {
      const response = await callAPI(
        "search-fee-history",
        {
          student_id: studentId,
          type: "all",
          limit: 50,
          page: 1,
        },
        "GET"
      );

      if (response.success && response.data) {
        const transformedHistory = response.data.map((record) => ({
          month: record.paid_month,
          fee_paid: record.type === "monthly" ? parseFloat(record.fee_paid) : 0,
          expense_paid:
            record.type === "expense" ? parseFloat(record.expense_paid) : 0,
          total_paid:
            record.type === "monthly"
              ? parseFloat(record.fee_paid)
              : parseFloat(record.expense_paid),
          total_pending:
            record.type === "monthly"
              ? parseFloat(record.fee_pending)
              : parseFloat(record.expense_pending),
          payment_date: record.created_at,
        }));

        setFeeHistory(transformedHistory);
      } else {
        setFeeHistory([]);
      }
    } catch (error) {
      console.error("Error fetching fee history:", error);
      setFeeHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check for existing records for the student and month
  const checkExistingRecords = async () => {
    if (!studentData.studentId || !startMonth) return;

    const paidMonth = moment(startMonth).format("MMMM YYYY");

    try {
      const monthlyFeeCheck = await callAPI("check-existing-fee", {
        student_id: studentData.studentId,
        paid_month: paidMonth,
        type: "monthly",
      });

      const expenseCheck = await callAPI("check-existing-fee", {
        student_id: studentData.studentId,
        paid_month: paidMonth,
        type: "expense",
      });

      const hasExisting = monthlyFeeCheck.exists || expenseCheck.exists;

      setExistingMonthlyFee(
        monthlyFeeCheck.exists ? monthlyFeeCheck.record : null
      );
      setExistingExpense(expenseCheck.exists ? expenseCheck.record : null);
      setExistingRecords({
        monthlyFee: monthlyFeeCheck.exists ? monthlyFeeCheck.record : null,
        expense: expenseCheck.exists ? expenseCheck.record : null,
      });
      setIsUpdateMode(hasExisting);
    } catch (error) {
      console.error("Error checking existing records:", error);
    }
  };

  // Helper function to process after student data is set
  const processAfterStudentData = async (fullStudentId) => {
    try {
      // Check for existing record in totalstdfeeandexp
      addApiLog({
        action: "check-totalstdfeeandexp",
        studentId: fullStudentId,
        status: "started",
      });

      const totalRecordCheck = await callAPI("check-totalstdfeeandexp", {
        student_id: fullStudentId,
      });

      addApiLog({
        action: "check-totalstdfeeandexp",
        studentId: fullStudentId,
        status: "success",
        data: totalRecordCheck,
      });

      if (totalRecordCheck.exists && totalRecordCheck.record) {
        setExistingTotalRecord(totalRecordCheck.record);
        setIsTotalRecordUpdateMode(true);

        const existing = totalRecordCheck.record;
        setStudentData((prev) => ({
          ...prev,
          annualCharges: parseFloat(existing.annual_charges) || 0,
          admissionFee: parseFloat(existing.admission_fee) || 0,
          workbookCharges: parseFloat(existing.workbook_charges) || 0,
          booksCharges: parseFloat(existing.book_charges) || 0,
          monthlyFee: parseFloat(existing.monthly_fee) || 0,
          totalPayable: parseFloat(existing.total_payable) || 0,
          discount: existing.discount || "0",
          discountPercentage: parseFloat(existing.discount) || 0,
          finalTotal: parseFloat(existing.final_total) || 0,
        }));

        setTotalFee(parseFloat(existing.total_fee) || 0);
        setTotalPaid(parseFloat(existing.total_paid) || 0);
        setLastPending(parseFloat(existing.last_pendings) || 0);

        Swal.fire({
          title: "Record Already Exists!",
          html: `
          <div class="ApplySoloStdFee-existingAlert">
            <FaInfoCircle style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
            <p>Fee and expense record for this student already exists in the system.</p>
            <div class="ApplySoloStdFee-existingDetails">
              <p><strong>Created:</strong> ${moment(existing.created_at).format(
                "DD-MM-YYYY"
              )}</p>
              <p><strong>Total Paid:</strong> ${formatCurrency(
                existing.total_paid
              )}</p>
              <p><strong>Last Pendings:</strong> ${formatCurrency(
                existing.last_pendings
              )}</p>
            </div>
            <p class="ApplySoloStdFee-updateNote">
              <FaEdit style="color: #3498db;" />
              You can still update all fields of the existing record.
            </p>
          </div>
        `,
          icon: "info",
          confirmButtonText: "Continue to Update",
          showCancelButton: true,
          cancelButtonText: "Cancel",
          customClass: {
            popup: "ApplySoloStdFee-swal-wide",
          },
        }).then((result) => {
          if (result.dismiss === Swal.DismissReason.cancel) {
            setIsTotalRecordUpdateMode(false);
            setExistingTotalRecord(null);
          }
        });
      } else {
        setExistingTotalRecord(null);
        setIsTotalRecordUpdateMode(false);
      }

      await checkExistingRecords();
      calculateTotalFee();
      calculateFinalTotal();

      setSystemStatus("student_loaded");
    } catch (totalRecordError) {
      console.error("Error checking total record:", totalRecordError);
      addApiLog({
        action: "check-totalstdfeeandexp",
        studentId: fullStudentId,
        status: "error",
        error: totalRecordError.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Validate if selected start month is appropriate
  const validateStartMonth = (selectedMonth) => {
    if (!studentData.admissionDate) return true;

    const admissionDate = moment(studentData.admissionDate).toDate();
    const selected = new Date(selectedMonth);

    // Selected month should not be before admission date
    if (selected < admissionDate) {
      Swal.fire({
        title: "Invalid Start Month",
        text: `Start month cannot be before admission date (${moment(
          admissionDate
        ).format("MMMM YYYY")})`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return false;
    }

    return true;
  };

  // Handle start month change
  const handleStartMonthChange = (date) => {
    if (validateStartMonth(date)) {
      const newStartMonth = new Date(date.getFullYear(), date.getMonth(), 1);
      setStartMonth(newStartMonth);
      
      // If end month is before start month, adjust it
      if (endMonth < newStartMonth) {
        const newEndMonth = new Date(newStartMonth);
        newEndMonth.setMonth(newEndMonth.getMonth() + 11);
        setEndMonth(newEndMonth);
      }
    }
  };

  // Handle end month change
  const handleEndMonthChange = (date) => {
    const newEndMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    
    if (newEndMonth < startMonth) {
      Swal.fire({
        title: "Invalid End Month",
        text: "End month cannot be before start month.",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }
    
    setEndMonth(newEndMonth);
  };

  // Handle search for student
  const handleSearch = async () => {
    if (isLoading) {
      return;
    }

    const fullStudentId = `Std-${studentIdPart2}-${studentIdPart3}`;
    setSearchError(null);

    if (!studentIdPart2 || !studentIdPart3) {
      Swal.fire({
        title: "Incomplete ID",
        text: "Please enter complete Student ID",
        icon: "warning",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    if (studentIdPart3.length < 4 || studentIdPart3.length > 8) {
      Swal.fire({
        title: "Invalid ID Format",
        text: "Student ID numeric part must be between 4 to 8 digits",
        icon: "warning",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    setIsLoading(true);
    setSystemStatus("searching");

    // Clear previous student data
    setStudentData({
      studentId: "",
      studentName: "",
      fatherName: "",
      studentClass: "",
      annualCharges: 0,
      admissionFee: 0,
      workbookCharges: 0,
      booksCharges: 0,
      monthlyFee: 0,
      totalPayable: 0,
      discount: "0",
      finalTotal: 0,
      mobile: "",
      bFormNo: "",
      address: "",
      gender: "",
      section: "",
      family_status: "",
      admissionDate: "",
      discountPercentage: 0,
    });
    setFeeHistory([]);
    setExistingTotalRecord(null);
    setIsTotalRecordUpdateMode(false);
    setIsFamilyStudent(false);
    setFamilyStudentData(null);
    setTotalFee(0);
    setTotalPaid("");
    setLastPending(0);
    setFinalTotal(0);
    setMonthCount(0);

    try {
      Swal.fire({
        title: "Searching...",
        html: `
        <div style="text-align: center;">
            <div class="ApplySoloStdFee-loading" style="margin: 0 auto 20px;"></div>
            <p>Searching for student: <strong>${fullStudentId}</strong></p>
            <p style="font-size: 12px; color: #666;">Please wait while we fetch student data...</p>
        </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });

      addApiLog({
        action: "search-student-fee",
        studentId: fullStudentId,
        status: "started",
      });

      const response = await callAPI("search-student-fee", {
        studentId: fullStudentId,
      });

      addApiLog({
        action: "search-student-fee",
        studentId: fullStudentId,
        status: "success",
        data: response,
      });

      // ========== IMPORTANT: Handle HTTP Errors First ==========
      if (response._isHttpError) {
        // Handle 404 - Student Not Found
        if (response._httpStatus === 404) {
          setIsLoading(false);
          Swal.close();
          Swal.fire({
            title: "Student Not Found",
            html: `
            <div style="text-align: center;">
                <FaUserTimes style="color: #e74c3c; font-size: 3rem; margin-bottom: 1rem;" />
                <h3>Student Not Found</h3>
                <p><strong>Student ID:</strong> ${fullStudentId}</p>
                <p>This student ID does not exist in the database.</p>
                <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin-top: 15px;">
                    <p style="margin: 5px 0; font-size: 12px; text-align: left;">
                        <strong>Possible reasons:</strong>
                    </p>
                    <ul style="text-align: left; font-size: 12px; margin: 10px 0; padding-left: 20px;">
                        <li>The student ID was entered incorrectly</li>
                        <li>The student record was deleted or archived</li>
                        <li>The student hasn't been registered yet</li>
                    </ul>
                </div>
            </div>
            `,
            icon: "error",
            confirmButtonText: "Try Another ID",
            customClass: {
              popup: "ApplySoloStdFee-swal-wide",
            },
          });
          resetForm();
          setSystemStatus("not_found");
          return;
        }

        // Check if this is a family student error (400 with isFamilyStudent flag)
        if (response._httpStatus === 400 && response.isFamilyStudent === true) {
          // FAMILY STUDENT DETECTED
          setIsFamilyStudent(true);
          setFamilyStudentData(response);
          showFamilyStudentModal(response);
          setIsLoading(false);
          Swal.close();
          return;
        }

        // Check if this is a fee setup validation error
        if (response._httpStatus === 400 && response.feeSetupValidation && !response.feeSetupValidation.isValid) {
          // FEE SETUP ERROR - But we still have student data
          handleFeeSetupError(response, fullStudentId);
          return;
        }

        // Other HTTP errors
        throw new Error(
          response.error || response.message || `HTTP Error ${response._httpStatus}`
        );
      }

      // ========== NORMAL SUCCESS RESPONSE ==========
      // Check if student is family student (shouldn't happen with new backend, but just in case)
      if (response.isFamilyStudent) {
        setIsFamilyStudent(true);
        setFamilyStudentData(response);
        showFamilyStudentModal(response);
        setIsLoading(false);
        Swal.close();
        return;
      }

      // Student is not family student and search was successful
      handleSuccessfulSearch(response, fullStudentId);

    } catch (error) {
      console.error("Fetch error:", error);
      setSearchError(error.message);

      addApiLog({
        action: "search-student-fee",
        studentId: fullStudentId,
        status: "error",
        error: error.message,
      });

      // Only show error modal if not a family student
      // (Family student case is already handled above)
      if (!isFamilyStudent) {
        Swal.fire({
          title: "Search Failed!",
          html: `
          <div style="text-align: center;">
              <FaExclamationTriangle style="color: #e74c3c; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>${error.message.includes("Fee setup") ? "Fee Setup Issue" : "Search Error"}</h3>
              <p><strong>Error:</strong> ${error.message || "Unknown error occurred"}</p>
              <p style="font-size: 14px; color: #666; margin-top: 10px;">
                  Student ID: <code>${fullStudentId}</code>
              </p>
          </div>
          `,
          icon: "error",
          confirmButtonText: "Try Again",
          customClass: {
            popup: "ApplySoloStdFee-swal-wide",
          },
        });
      }

      resetForm();
      setSystemStatus("error");
      setIsLoading(false);
      Swal.close();
    }
  };

  // Helper function to handle fee setup errors
  const handleFeeSetupError = (response, fullStudentId) => {
    setIsFamilyStudent(false);
    setFamilyStudentData(null);

    // Extract student and fee details from response
    const student = response.student || {};
    const feeDetails = response.feeDetails || {};

    const updatedStudentData = {
      studentId: fullStudentId,
      studentName: student.name || "",
      fatherName: student.father_name || "",
      studentClass: student.class || "",
      annualCharges: parseFloat(feeDetails.annual_charges) || 0,
      admissionFee: parseFloat(feeDetails.admission_fee) || 0,
      workbookCharges: parseFloat(feeDetails.workbook_charges) || 0,
      booksCharges: parseFloat(feeDetails.book_charges) || 0,
      monthlyFee: parseFloat(feeDetails.monthly_fee) || 0,
      totalPayable: parseFloat(feeDetails.total_payable) || 0,
      discount: student.discount || "0",
      discountPercentage: parseFloat(student.discount) || 0,
      finalTotal: parseFloat(feeDetails.total_payable) || 0,
      mobile: student.mobile || "",
      bFormNo: student.b_form_no || "",
      address: student.address || "",
      gender: student.gender || "",
      section: student.section || "",
      family_status: student.family_status || "",
      admissionDate: student.admission_date || "",
    };

    setStudentData(updatedStudentData);

    // Calculate start month based on admission date or current month
    const calculatedStartMonth = calculateStartMonth(student.admission_date);
    setStartMonth(calculatedStartMonth);

    // Set end month to be 12 months from start
    const calculatedEndMonth = new Date(calculatedStartMonth);
    calculatedEndMonth.setMonth(calculatedEndMonth.getMonth() + 11);
    setEndMonth(calculatedEndMonth);

    Swal.close();

    // Show warning but don't prevent form display
    Swal.fire({
      title: "Fee Setup Incomplete",
      html: `
      <div style="text-align: center;">
          <FaExclamationTriangle style="color: #f39c12; font-size: 3rem; margin-bottom: 1rem;" />
          <h3>Fee Setup Incomplete</h3>
          <p><strong>Student found:</strong> ${student.name || "N/A"} (${fullStudentId})</p>
          <p>Student data loaded successfully, but fee setup needs to be completed.</p>
          <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin-top: 15px;">
              <p style="margin: 5px 0; font-size: 14px;"><strong>Issue:</strong> ${response.feeSetupValidation?.message || "Fee setup not found"}</p>
              <p style="margin: 5px 0; font-size: 12px; text-align: left;">
                  <strong>Action Required:</strong> ${response.actionRequired || "Please complete the fee setup below or in the fee setup section"}
              </p>
          </div>
      </div>
      `,
      icon: "warning",
      confirmButtonText: "Continue to Complete Fee Setup",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    }).then(() => {
      // Continue with existing record check and calculations
      processAfterStudentData(fullStudentId);
    });
  };

  // Helper function to handle successful search
  const handleSuccessfulSearch = (response, fullStudentId) => {
    setIsFamilyStudent(false);
    setFamilyStudentData(null);

    const student = response.student || {};
    const feeDetails = response.feeDetails || {};

    const updatedStudentData = {
      studentId: fullStudentId,
      studentName: student.name || "",
      fatherName: student.father_name || "",
      studentClass: student.class || "",
      annualCharges: parseFloat(feeDetails.annual_charges) || 0,
      admissionFee: parseFloat(feeDetails.admission_fee) || 0,
      workbookCharges: parseFloat(feeDetails.workbook_charges) || 0,
      booksCharges: parseFloat(feeDetails.book_charges) || 0,
      monthlyFee: parseFloat(feeDetails.monthly_fee) || 0,
      totalPayable: parseFloat(feeDetails.total_payable) || 0,
      discount: student.discount || "0",
      discountPercentage: parseFloat(student.discount) || 0,
      finalTotal: parseFloat(feeDetails.total_payable) || 0,
      mobile: student.mobile || "",
      bFormNo: student.b_form_no || "",
      address: student.address || "",
      gender: student.gender || "",
      section: student.section || "",
      family_status: student.family_status || "",
      admissionDate: student.admission_date || "",
    };

    setStudentData(updatedStudentData);

    // Calculate start month based on admission date or current month
    const calculatedStartMonth = calculateStartMonth(student.admission_date);
    setStartMonth(calculatedStartMonth);

    // Set end month to be 12 months from start
    const calculatedEndMonth = new Date(calculatedStartMonth);
    calculatedEndMonth.setMonth(calculatedEndMonth.getMonth() + 11);
    setEndMonth(calculatedEndMonth);

    Swal.close();

    // Process existing records and calculations
    processAfterStudentData(fullStudentId);

    // Show success message
    Swal.fire({
      title: "Student Found!",
      html: `
      <div style="text-align: center;">
          <FaCheckCircle style="color: #27ae60; font-size: 3rem; margin-bottom: 1rem;" />
          <h3>Student Found!</h3>
          <p><strong>${student.name || "Student data"}</strong> loaded successfully.</p>
          <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin-top: 15px;">
              <p style="margin: 5px 0;"><strong>ID:</strong> ${fullStudentId}</p>
              <p style="margin: 5px 0;"><strong>Class:</strong> ${student.class || ""} - ${student.section || ""}</p>
              <p style="margin: 5px 0;"><strong>Monthly Fee:</strong> ${formatCurrency(feeDetails.monthly_fee || 0)}</p>
              <p style="margin: 5px 0;"><strong>Start Month Set To:</strong> ${moment(calculatedStartMonth).format("MMMM YYYY")}</p>
              ${student.admission_date
                ? `<p style="margin: 5px 0;"><strong>Admission Date:</strong> ${moment(student.admission_date).format("DD-MM-YYYY")}</p>`
                : ""
              }
          </div>
      </div>
      `,
      icon: "success",
      confirmButtonText: "Continue",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    });

    setSystemStatus("student_loaded");
  };

  // Reset form
  const resetForm = () => {
    setStudentData({
      studentId: "",
      studentName: "",
      fatherName: "",
      studentClass: "",
      annualCharges: 0,
      admissionFee: 0,
      workbookCharges: 0,
      booksCharges: 0,
      monthlyFee: 0,
      totalPayable: 0,
      discount: "0",
      discountPercentage: 0,
      finalTotal: 0,
      mobile: "",
      bFormNo: "",
      address: "",
      gender: "",
      section: "",
      family_status: "",
      admissionDate: "",
    });
    setTotalFee(0);
    setFinalTotal(0);
    setTotalPaid("");
    setLastPending(0);
    setMonthCount(0);
    setFeeHistory([]);
    setExistingMonthlyFee(null);
    setExistingExpense(null);
    setExistingRecords(null);
    setIsUpdateMode(false);
    setExistingTotalRecord(null);
    setIsTotalRecordUpdateMode(false);
    setSearchError(null);
    setSystemStatus("ready");
    setIsFamilyStudent(false);
    setFamilyStudentData(null);

    // Reset months to current
    const today = new Date();
    const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartMonth(startMonth);

    const nextYear = new Date(startMonth);
    nextYear.setMonth(nextYear.getMonth() + 11);
    setEndMonth(nextYear);
  };

  // Show student fee history modal
  const showFeeHistoryModal = () => {
    if (feeHistory.length === 0) {
      Swal.fire({
        title: "No Fee History",
        text: "No fee payment history available for this student.",
        icon: "info",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    let historyHTML = `
      <div class="ApplySoloStdFee-feeHistoryModal">
        <h4>Fee Payment History for ${studentData.studentName}</h4>
        <div class="ApplySoloStdFee-feeHistoryTable">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Monthly Fee Paid</th>
                <th>Expense Paid</th>
                <th>Total Paid</th>
                <th>Pending</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
    `;

    feeHistory.forEach((payment) => {
      historyHTML += `
        <tr>
          <td>${payment.month}</td>
          <td>${formatCurrency(payment.fee_paid)}</td>
          <td>${formatCurrency(payment.expense_paid)}</td>
          <td>${formatCurrency(payment.total_paid)}</td>
          <td class="${payment.total_pending > 0 ? "pending" : "paid"}">
            ${formatCurrency(payment.total_pending)}
          </td>
          <td>${moment(payment.payment_date).format("DD-MM-YYYY")}</td>
        </tr>
      `;
    });

    historyHTML += `
            </tbody>
          </table>
        </div>
      </div>
    `;

    Swal.fire({
      title: "Fee History",
      html: historyHTML,
      width: "900px",
      showCloseButton: true,
      confirmButtonText: "Close",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    });
  };

  // Show student details modal
  const showStudentDetailsModal = () => {
    if (!studentData.studentId) {
      Swal.fire({
        title: "No Data",
        text: "No student data available!",
        icon: "error",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    Swal.fire({
      title: "Student Details",
      html: `
        <div class="ApplySoloStdFee-studentModal">
          <div class="ApplySoloStdFee-studentHeader">
            <div class="ApplySoloStdFee-studentAvatar">
              ${studentData.studentName.charAt(0)}
            </div>
            <div class="ApplySoloStdFee-studentBasic">
              <h3>${studentData.studentName}</h3>
              <p>${studentData.studentId}</p>
            </div>
          </div>
          
          <div class="ApplySoloStdFee-studentGrid">
            <div class="ApplySoloStdFee-studentField">
              <label>Father Name</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.fatherName
              }</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Class & Section</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.studentClass
              } - ${studentData.section}</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Mobile</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.mobile || "N/A"
              }</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>B-Form No</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.bFormNo || "N/A"
              }</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Address</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.address || "N/A"
              }</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Gender</label>
              <div class="ApplySoloStdFee-studentValue">${
                studentData.gender || "N/A"
              }</div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Family Status</label>
              <div class="ApplySoloStdFee-studentValue">
                <span class="ApplySoloStdFee-statusBadge ${studentData.family_status
                  ?.toLowerCase()
                  .replace(" ", "-")}">
                  ${studentData.family_status}
                </span>
              </div>
            </div>
            
            <div class="ApplySoloStdFee-studentField">
              <label>Admission Date</label>
              <div class="ApplySoloStdFee-studentValue">
                ${
                  studentData.admissionDate
                    ? moment(studentData.admissionDate).format("DD-MM-YYYY")
                    : "N/A"
                }
              </div>
            </div>
          </div>
          
          <div class="ApplySoloStdFee-feeSummary">
            <div class="ApplySoloStdFee-feeSummaryItem">
              <label>Monthly Fee</label>
              <div class="ApplySoloStdFee-feeAmount">${formatCurrency(
                studentData.monthlyFee
              )}</div>
            </div>
            
            <div class="ApplySoloStdFee-feeSummaryItem">
              <label>Discount</label>
              <div class="ApplySoloStdFee-feeAmount">${
                studentData.discountPercentage
              }%</div>
            </div>
          </div>
        </div>
      `,
      width: "700px",
      showCloseButton: true,
      confirmButtonText: "Close",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    });
  };

  // Show update confirmation dialog
  const showUpdateConfirmation = async (totalRecordData, paidAmount) => {
    return Swal.fire({
      title: "Update Existing Record?",
      html: `
        <div class="ApplySoloStdFee-updateConfirmModal">
          <div class="ApplySoloStdFee-updateConfirmHeader">
            <FaEdit style="color: #f39c12; font-size: 2rem; margin-bottom: 1rem;" />
            <h3>Update Fee Record</h3>
            <p>This will update the existing fee and expense record for ${
              studentData.studentName
            }.</p>
          </div>
          
          <div class="ApplySoloStdFee-updateConfirmDetails">
            <div class="ApplySoloStdFee-confirmItem">
              <span>Student:</span>
              <strong>${studentData.studentName}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Student ID:</span>
              <strong>${studentData.studentId}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Total Payable:</span>
              <strong>${formatCurrency(studentData.totalPayable)}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Discount:</span>
              <strong>${studentData.discountPercentage}%</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Final Total:</span>
              <strong>${formatCurrency(finalTotal)}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Amount to Update:</span>
              <strong>${formatCurrency(paidAmount)}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>New Balance:</span>
              <strong>${formatCurrency(lastPending)}</strong>
            </div>
          </div>
          
          <div class="ApplySoloStdFee-updateNote">
            <FaInfoCircle style="color: #3498db;" />
            <span>All fields in the record will be updated with the new values.</span>
          </div>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Update Record",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
      width: "600px",
    });
  };

  // Show submit confirmation dialog
  const showSubmitConfirmation = async (totalRecordData, paidAmount) => {
    return Swal.fire({
      title: "Submit New Record?",
      html: `
        <div class="ApplySoloStdFee-submitConfirmModal">
          <div class="ApplySoloStdFee-submitConfirmHeader">
            <FaPaperPlane style="color: #27ae60; font-size: 2rem; margin-bottom: 1rem;" />
            <h3>Submit New Fee Record</h3>
            <p>This will create a new fee and expense record for ${
              studentData.studentName
            }.</p>
          </div>
          
          <div class="ApplySoloStdFee-submitConfirmDetails">
            <div class="ApplySoloStdFee-confirmItem">
              <span>Student:</span>
              <strong>${studentData.studentName}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Total Payable:</span>
              <strong>${formatCurrency(studentData.totalPayable)}</strong>
            </div>
            
            <div class="ApplySoloStdFee-confirmItem">
              <span>Amount to Submit:</span>
              <strong>${formatCurrency(paidAmount)}</strong>
            </div>
          </div>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit New Record",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
      width: "600px",
    });
  };

  // Process update submission
  const processUpdateSubmission = async (totalRecordData) => {
    setIsSubmitting(true);
    setSystemStatus("updating");

    try {
      Swal.fire({
        title: "Updating Record...",
        html: `
          <div style="text-align: center;">
            <div class="ApplySoloStdFee-loading" style="margin: 0 auto 20px;"></div>
            <p>Updating record for <strong>${studentData.studentName}</strong></p>
            <p style="font-size: 12px; color: #666;">Please wait while we update the record...</p>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });

      if (!existingTotalRecord?.id) {
        throw new Error("Missing record ID for update");
      }

      const updateData = {
        id: existingTotalRecord.id,
        ...totalRecordData,
      };

      addApiLog({
        action: "update-totalstdfeeandexp",
        data: updateData,
        status: "started",
      });

      const updateResponse = await callAPI(
        "update-totalstdfeeandexp",
        updateData,
        "POST"
      );

      addApiLog({
        action: "update-totalstdfeeandexp",
        data: updateData,
        status: "success",
        response: updateResponse,
      });

      Swal.close();

      if (updateResponse.success) {
        Swal.fire({
          title: "Record Updated!",
          html: `
            <div class="ApplySoloStdFee-successModal">
              <FaCheckCircle style="color: #f39c12; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>Fee Record Updated Successfully!</h3>
              <p>Existing record has been updated with new values.</p>
              
              <div class="ApplySoloStdFee-updateSummary">
                <div><strong>Total Paid Updated:</strong> ${formatCurrency(
                  totalRecordData.total_paid
                )}</div>
                <div><strong>New Balance:</strong> ${formatCurrency(
                  totalRecordData.last_pendings
                )}</div>
                <div><strong>Record ID:</strong> ${existingTotalRecord.id}</div>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            popup: "ApplySoloStdFee-swal-wide",
          },
        }).then(() => {
          resetForm();
          setStudentIdPart2("");
          setStudentIdPart3("");
        });
      } else {
        throw new Error(updateResponse.error || "Failed to update record");
      }
    } catch (error) {
      console.error("Update submission error:", error);
      addApiLog({
        action: "update-totalstdfeeandexp",
        status: "error",
        error: error.message,
      });

      Swal.close();

      Swal.fire({
        title: "Update Failed!",
        html: `
          <div class="ApplySoloStdFee-errorModal">
            <FaExclamationTriangle style="color: #e74c3c; font-size: 3rem; margin-bottom: 1rem;" />
            <h3>Update Failed</h3>
            <p>${
              error.message || "An error occurred while updating the record."
            }</p>
            <div class="ApplySoloStdFee-errorDetails">
              <p><strong>Error Details:</strong></p>
              <code style="font-size: 12px; background: #f8f9fa; padding: 10px; display: block; border-radius: 4px; margin-top: 10px;">
                ${error.toString()}
              </code>
            </div>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
    } finally {
      setIsSubmitting(false);
      setSystemStatus("ready");
    }
  };

  // Process new submission
  const processNewSubmission = async (totalRecordData) => {
    setIsSubmitting(true);
    setSystemStatus("submitting");

    try {
      Swal.fire({
        title: "Submitting Record...",
        html: `
          <div style="text-align: center;">
            <div class="ApplySoloStdFee-loading" style="margin: 0 auto 20px;"></div>
            <p>Creating new record for <strong>${studentData.studentName}</strong></p>
            <p style="font-size: 12px; color: #666;">Please wait while we create the record...</p>
          </div>
        `,
        allowOutsideClick: false,
        showConfirmButton: false,
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });

      addApiLog({
        action: "submit-solo-fee",
        data: totalRecordData,
        status: "started",
      });

      const submitResponse = await callAPI("submit-solo-fee", totalRecordData);

      addApiLog({
        action: "submit-solo-fee",
        data: totalRecordData,
        status: "success",
        response: submitResponse,
      });

      Swal.close();

      if (submitResponse.success) {
        Swal.fire({
          title: "Record Submitted!",
          html: `
            <div class="ApplySoloStdFee-successModal">
              <FaCheckCircle style="color: #27ae60; font-size: 3rem; margin-bottom: 1rem;" />
              <h3>New Fee Record Created Successfully!</h3>
              <p>A new fee and expense record has been created.</p>
              
              <div class="ApplySoloStdFee-submitSummary">
                <div><strong>Record ID:</strong> ${submitResponse.fee_id}</div>
                <div><strong>Total Paid:</strong> ${formatCurrency(
                  totalRecordData.total_paid
                )}</div>
                <div><strong>Month:</strong> ${submitResponse.paid_month}</div>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            popup: "ApplySoloStdFee-swal-wide",
          },
        }).then(() => {
          resetForm();
          setStudentIdPart2("");
          setStudentIdPart3("");
        });
      } else {
        throw new Error(submitResponse.error || "Failed to submit record");
      }
    } catch (error) {
      console.error("Submission error:", error);
      addApiLog({
        action: "submit-solo-fee",
        status: "error",
        error: error.message,
      });

      Swal.close();

      Swal.fire({
        title: "Submission Failed!",
        text: error.message || "An error occurred while creating the record.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
    } finally {
      setIsSubmitting(false);
      setSystemStatus("ready");
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - Check if family student
    if (isFamilyStudent) {
      Swal.fire({
        title: "Family Student Detected",
        text: "Family students cannot use this screen. Please use Family Fee Management.",
        icon: "error",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    if (!studentData.studentId || !studentData.studentName) {
      Swal.fire({
        title: "Missing Information",
        text: "Please search for a student first.",
        icon: "warning",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    const monthlyFee = parseFloat(studentData.monthlyFee) || 0;
    const paidAmount = parseFloat(totalPaid) || 0;
    const totalPayable = parseFloat(studentData.totalPayable) || 0;

    if (paidAmount <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "Please enter a valid payment amount.",
        icon: "warning",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    if (paidAmount > totalPayable) {
      Swal.fire({
        title: "Overpayment Detected!",
        text: `Total Paid (${formatCurrency(
          paidAmount
        )}) cannot exceed Total Payable (${formatCurrency(totalPayable)})!`,
        icon: "warning",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
      return;
    }

    const discountPercentage = parseFloat(studentData.discountPercentage) || 0;
    const discountAmount = (totalPayable * discountPercentage) / 100;
    const finalTotalAmount = totalPayable - discountAmount;
    const lastPendings = finalTotalAmount - paidAmount;

    const totalRecordData = {
      student_id: studentData.studentId,
      student_name: studentData.studentName,
      family_name: studentData.fatherName,
      family_code:
        studentData.family_status === "Family" ? studentData.studentId : "",
      annual_charges: studentData.annualCharges,
      admission_fee: studentData.admissionFee,
      workbook_charges: studentData.workbookCharges,
      book_charges: studentData.booksCharges,
      monthly_fee: monthlyFee,
      total_fee: totalFee,
      total_payable: totalPayable,
      discount: discountPercentage,
      final_total: finalTotalAmount,
      total_paid: paidAmount,
      last_pendings: lastPendings,
    };

    if (isTotalRecordUpdateMode && existingTotalRecord) {
      const result = await showUpdateConfirmation(totalRecordData, paidAmount);
      if (!result.isConfirmed) return;
      await processUpdateSubmission(totalRecordData);
    } else {
      const result = await showSubmitConfirmation(totalRecordData, paidAmount);
      if (!result.isConfirmed) return;
      await processNewSubmission(totalRecordData);
    }
  };

  // Calculate fees summary
  const calculateSummary = () => {
    const monthlyFee = parseFloat(studentData.monthlyFee) || 0;
    const totalOneTimeCharges =
      (parseFloat(studentData.annualCharges) || 0) +
      (parseFloat(studentData.admissionFee) || 0) +
      (parseFloat(studentData.workbookCharges) || 0) +
      (parseFloat(studentData.booksCharges) || 0);

    return {
      monthlyFee,
      oneTimeCharges: totalOneTimeCharges,
      totalFee,
      totalPayable: parseFloat(studentData.totalPayable) || 0,
      discount: parseFloat(studentData.discountPercentage) || 0,
      discountAmount:
        ((parseFloat(studentData.totalPayable) || 0) *
          (parseFloat(studentData.discountPercentage) || 0)) /
        100,
      finalTotal,
      paid: parseFloat(totalPaid) || 0,
      balance: lastPending,
      monthCount: monthCount,
    };
  };

  const summary = calculateSummary();

  // Handle print receipt
  const handlePrintReceipt = async () => {
    if (isFamilyStudent) {
      Swal.fire({ title: "Error", text: "Please use Family Fee Management.", icon: "error" });
      return;
    }

    if (!studentData.studentId || !totalPaid) {
      Swal.fire({ title: "No Payment", text: "Please complete a payment first.", icon: "warning" });
      return;
    }

    setIsSubmitting(true);

    try {
      const { default: jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");

      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Convert vh to mm (1vh is 1% of page height)
      const vh = pageHeight / 100;

      const loadImage = (url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = url;
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
        });
      };

      const images = await Promise.all([
        loadImage('/images/logo.jpg'),
        loadImage('/images/l2.png'),
        loadImage('/images/stamp.png')
      ]);
      const [logoLeft, logoRight, stampImg] = images;

      const drawReceipt = (startY, typeLabel) => {
        const sectionHeight = pageHeight / 2;
        
        // 1. Decorative Bubbles
        doc.setGState(new doc.GState({ opacity: 0.03 }));
        const colors = [[44, 50, 146], [230, 126, 34], [39, 174, 96]];
        for (let i = 0; i < 6; i++) {
          const color = colors[i % 3];
          doc.setFillColor(color[0], color[1], color[2]);
          doc.circle(Math.random() * pageWidth, startY + (Math.random() * sectionHeight), 10 + Math.random() * 20, 'F');
        }
        doc.setGState(new doc.GState({ opacity: 1.0 }));

        // 2. Header
        const headerH = 30;
        doc.setFillColor(255, 255, 255);
        doc.rect(0, startY, pageWidth, headerH, 'F');
        doc.setFillColor(255, 215, 0); 
        doc.rect(0, startY, pageWidth, 2, 'F');

        if (logoLeft) doc.addImage(logoLeft, 'JPEG', margin, startY + 5, 20, 20);
        if (logoRight) doc.addImage(logoRight, 'PNG', pageWidth - margin - 20, startY + 5, 20, 20);

        doc.setTextColor(44, 50, 146);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('DAR-E-ARQAM SCHOOL', pageWidth / 2, startY + 12, { align: 'center' });
        doc.setFontSize(9);
        doc.text('KOTLI LOHARAN EAST CAMPUS | FEE RECEIPT', pageWidth / 2, startY + 18, { align: 'center' });

        // 3. Receipt Info Bar
        const infoY = startY + headerH + 5;
        doc.setFillColor(245, 247, 255);
        doc.roundedRect(margin, infoY, pageWidth - (margin * 2), 12, 2, 2, 'F');
        
        doc.setFontSize(10);
        doc.text(typeLabel, margin + 5, infoY + 8);
        doc.setFont('helvetica', 'normal');
        doc.text(`Receipt: REC-${Date.now().toString().slice(-6)}`, pageWidth / 2, infoY + 8, { align: 'center' });
        doc.text(`Date: ${moment().format("DD-MM-YYYY")}`, pageWidth - margin - 5, infoY + 8, { align: 'right' });

        // 4. Student Details
        const detY = infoY + 18;
        doc.setFontSize(9);
        doc.text(`Student ID:  ${studentData.studentId}`, margin, detY);
        doc.text(`Class:           ${studentData.studentClass} - ${studentData.section}`, margin, detY + 6);
        doc.text(`Student:       ${studentData.studentName}`, margin + 80, detY);
        doc.text(`Father:         ${studentData.fatherName}`, margin + 80, detY + 6);

        // 5. Period Information
        const periodY = detY + 12;
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Period: ${moment(startMonth).format("MMMM YYYY")} to ${moment(endMonth).format("MMMM YYYY")} (${monthCount} months)`, margin, periodY);

        // 6. Fee Table
        autoTable(doc, {
          startY: periodY + 5,
          margin: { left: margin, right: margin },
          head: [['Description', 'Amount (Rs.)']],
          body: [
            ['Monthly Fee (' + monthCount + ' Months)', formatCurrency(totalFee).replace("Rs. ", "")],
            ['One-Time Charges', formatCurrency(summary.oneTimeCharges).replace("Rs. ", "")],
            ['Gross Total', formatCurrency(summary.totalPayable).replace("Rs. ", "")],
            ['Discount (' + studentData.discountPercentage + '%)', '-' + formatCurrency(summary.discountAmount).replace("Rs. ", "")],
            [{ content: 'Net Total', styles: { fontStyle: 'bold' } }, { content: formatCurrency(finalTotal).replace("Rs. ", ""), styles: { fontStyle: 'bold' } }],
            [{ content: 'Amount Paid', styles: { fontStyle: 'bold', textColor: [39, 174, 96] } }, { content: formatCurrency(totalPaid).replace("Rs. ", ""), styles: { fontStyle: 'bold', textColor: [39, 174, 96] } }],
            [{ content: 'Balance Due', styles: { fontStyle: 'bold', textColor: [231, 76, 60] } }, { content: formatCurrency(lastPending).replace("Rs. ", ""), styles: { fontStyle: 'bold', textColor: [231, 76, 60] } }],
          ],
          theme: 'grid',
          headStyles: { fillColor: [44, 50, 146], textColor: 255, halign: 'center' },
          columnStyles: { 1: { halign: 'right' } },
          styles: { fontSize: 8, cellPadding: 2 }
        });

        // 7. Footer, Signature & Stamp
        const finalTableY = doc.lastAutoTable.finalY;
        const footerY = finalTableY + (5 * vh);
        
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text("Computer generated receipt. Valid only with official school stamp.", margin, footerY + 10);
        
        // Signature Line
        doc.setDrawColor(200);
        doc.line(pageWidth - margin - 45, footerY, pageWidth - margin, footerY);
        doc.text("Cashier Signature", pageWidth - margin - 22.5, footerY + 4, { align: 'center' });

        // Stamp Placement
        if (stampImg) {
          doc.setGState(new doc.GState({ opacity: 0.8 }));
          const stampX = margin - (3 * vh);
          doc.addImage(stampImg, 'PNG', stampX, footerY - 15, 35, 25);
          doc.setGState(new doc.GState({ opacity: 1.0 }));
        }
      };

      drawReceipt(0, "OFFICE COPY");
      
      doc.setLineDash([2, 2], 0);
      doc.setDrawColor(180);
      doc.line(0, pageHeight / 2, pageWidth, pageHeight / 2);
      doc.setLineDash([], 0); 

      drawReceipt(pageHeight / 2, "STUDENT/CUSTOMER COPY");

      doc.save(`Fee_Receipt_${studentData.studentId}.pdf`);
      Swal.fire({ title: "Success", text: "Receipt generated successfully!", icon: "success" });

    } catch (error) {
      console.error("PDF Error:", error);
      Swal.fire({ title: "Error", text: "Failed to generate PDF.", icon: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear form
  const handleClearForm = () => {
    Swal.fire({
      title: "Clear Form?",
      text: "This will reset all entered data.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        resetForm();
        setStudentIdPart2("");
        setStudentIdPart3("");
      }
    });
  };

  // Handle key navigation
  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (index === 0 && studentIdPart2.length === 3) {
        inputRefs.current[1]?.focus();
      } else if (
        index === 1 &&
        studentIdPart3.length >= 4 &&
        studentIdPart3.length <= 8
      ) {
        handleSearch();
      }
    }
  };

  // Test API functionality
  const testApiConnection = async () => {
    try {
      Swal.fire({
        title: "Testing Connection...",
        text: "Testing API connectivity...",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await callAPI("debug-fee-tables", {}, "GET");

      Swal.close();

      Swal.fire({
        title: "API Test Results",
        html: `
          <div style="text-align: left; max-height: 400px; overflow-y: auto;">
            <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(response, null, 2)}
            </pre>
          </div>
        `,
        width: "800px",
        confirmButtonText: "Close",
        customClass: {
          popup: "ApplySoloStdFee-swal-wide",
        },
      });
    } catch (error) {
      Swal.close();
      Swal.fire({
        title: "API Test Failed",
        text: error.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  // Show debug panel
  const showDebugPanel = () => {
    Swal.fire({
      title: "Debug Information",
      html: `
        <div style="text-align: left; max-height: 400px; overflow-y: auto;">
          <h4>System Status: ${systemStatus}</h4>
          <h4>Student Data:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(studentData, null, 2)}
          </pre>
          <h4>Family Student Status: ${isFamilyStudent ? "YES" : "NO"}</h4>
          <h4>Month Calculation:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
Start: ${moment(startMonth).format("MMMM YYYY")}
End: ${moment(endMonth).format("MMMM YYYY")}
Months: ${monthCount}
Total Fee: ${totalFee}
          </pre>
          <h4>Recent API Logs:</h4>
          <pre style="background: #f8f9fa; padding: 10px; border-radius: 5px; font-size: 12px;">
${JSON.stringify(apiLogs.slice(0, 5), null, 2)}
          </pre>
        </div>
      `,
      width: "800px",
      confirmButtonText: "Close",
      showCancelButton: true,
      cancelButtonText: "Clear Logs",
      customClass: {
        popup: "ApplySoloStdFee-swal-wide",
      },
    }).then((result) => {
      if (result.dismiss === Swal.DismissReason.cancel) {
        setApiLogs([]);
      }
    });
  };

  // Handle input changes
  const handleStudentIdPart2Change = (value) => {
    const cleanedValue = value
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 3);
    setStudentIdPart2(cleanedValue);
  };

  const handleStudentIdPart3Change = (value) => {
    const cleanedValue = value.replace(/[^0-9]/g, "").slice(0, 8);
    setStudentIdPart3(cleanedValue);
  };

  // Get month range info for display
  const getMonthRangeInfo = () => {
    if (!startMonth || !endMonth) return "";
    
    const start = moment(startMonth);
    const end = moment(endMonth);
    const months = [];
    
    for (let m = moment(start); m.isSameOrBefore(end, 'month'); m.add(1, 'month')) {
      months.push(m.format("MMM"));
    }
    
    return `${months.length} months (${months.join(", ")})`;
  };

  return (
    <div className="ApplySoloStdFee-container">
      <Sidebar />

      <div className="ApplySoloStdFee-main-content">
        {/* Debug Panel */}
        {debugMode && (
          <div className="ApplySoloStdFee-debugPanel">
            <div className="ApplySoloStdFee-debugHeader">
              <FaBug className="ApplySoloStdFee-debugIcon" />
              <h3>Debug Panel</h3>
              <button
                className="ApplySoloStdFee-debugClose"
                onClick={() => setDebugMode(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="ApplySoloStdFee-debugContent">
              <div className="ApplySoloStdFee-debugSection">
                <h4>
                  System Status:{" "}
                  <span className={`ApplySoloStdFee-status-${systemStatus}`}>
                    {systemStatus}
                  </span>
                </h4>
                <h4>
                  Family Student:{" "}
                  <span
                    className={
                      isFamilyStudent
                        ? "ApplySoloStdFee-status-warning"
                        : "ApplySoloStdFee-status-success"
                    }
                  >
                    {isFamilyStudent ? "YES" : "NO"}
                  </span>
                </h4>
              </div>
              <div className="ApplySoloStdFee-debugSection">
                <h4>API Logs ({apiLogs.length})</h4>
                <div className="ApplySoloStdFee-apiLogs">
                  {apiLogs.slice(0, 10).map((log, index) => (
                    <div
                      key={index}
                      className={`ApplySoloStdFee-apiLog ApplySoloStdFee-apiLog-${log.status}`}
                    >
                      <div className="ApplySoloStdFee-apiLog-time">
                        {moment(log.timestamp).format("HH:mm:ss")}
                      </div>
                      <div className="ApplySoloStdFee-apiLog-action">
                        {log.action}
                      </div>
                      <div className="ApplySoloStdFee-apiLog-status">
                        {log.status}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <header className="ApplySoloStdFee-header">
          <div className="ApplySoloStdFee-headerContent">
            <h1 className="ApplySoloStdFee-title" style={{color:'white'}}>
              <FaMoneyBillWave className="ApplySoloStdFee-titleIcon" />
              Student Fee Application
            </h1>
            <p className="ApplySoloStdFee-subtitle">
              Apply fees and expenses for individual students
            </p>
            <div className="ApplySoloStdFee-systemInfo">
              <span
                className={`ApplySoloStdFee-systemStatus ApplySoloStdFee-systemStatus-${systemStatus}`}
              >
                {systemStatus.replace("_", " ")}
              </span>
              <span className="ApplySoloStdFee-systemTime">
                {moment().format("DD-MM-YYYY HH:mm")}
              </span>
            </div>
          </div>
          <div className="ApplySoloStdFee-headerActions">
            <button
              className="ApplySoloStdFee-debugBtn"
              onClick={() => setDebugMode(!debugMode)}
              title="Toggle Debug Mode"
            >
              <FaBug />
            </button>
            <button
              className="ApplySoloStdFee-testBtn"
              onClick={testApiConnection}
              title="Test API Connection"
            >
              <FaDatabase />
            </button>
          </div>
        </header>

        {/* Search Section */}
        <section className="ApplySoloStdFee-searchSection">
          <div className="ApplySoloStdFee-searchCard">
            <div className="ApplySoloStdFee-searchHeader">
              <FaSearch className="ApplySoloStdFee-searchIcon" />
              <h2>Search Student</h2>
              <div className="ApplySoloStdFee-classBadge">{className}</div>
            </div>

            <div className="ApplySoloStdFee-searchForm">
              <div className="ApplySoloStdFee-idInputGroup">
                <label className="ApplySoloStdFee-idLabel">
                  <FaIdCard className="ApplySoloStdFee-idLabelIcon" />
                  Enter Student ID
                </label>

                <div className="ApplySoloStdFee-idInput">
                  <div className="ApplySoloStdFee-idPrefix">Std</div>
                  <span className="ApplySoloStdFee-idSeparator">-</span>
                  <input
                    type="text"
                    className="ApplySoloStdFee-idField"
                    value={studentIdPart2}
                    onChange={(e) => handleStudentIdPart2Change(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 0)}
                    placeholder="PG"
                    maxLength="3"
                    autoFocus
                    ref={(el) => (inputRefs.current[0] = el)}
                  />
                  <span className="ApplySoloStdFee-idSeparator">-</span>
                  <input
                    type="text"
                    className="ApplySoloStdFee-idField"
                    value={studentIdPart3}
                    onChange={(e) => handleStudentIdPart3Change(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 1)}
                    placeholder="260001"
                    maxLength="8"
                    ref={(el) => (inputRefs.current[1] = el)}
                  />
                </div>

                <div className="ApplySoloStdFee-idHint">
                  Format: Std-XXX-XXXXXXXX (e.g., Std-PG-260001, Std-PG-2650001)
                </div>
              </div>

              {searchError && (
                <div className="ApplySoloStdFee-searchError">
                  <FaExclamationTriangle className="ApplySoloStdFee-errorIcon" />
                  <span>{searchError}</span>
                </div>
              )}

              <div className="ApplySoloStdFee-searchActions">
                <button
                  className="ApplySoloStdFee-searchBtn"
                  onClick={handleSearch}
                  disabled={
                    isLoading ||
                    !studentIdPart2 ||
                    !studentIdPart3 ||
                    studentIdPart3.length < 4 ||
                    studentIdPart3.length > 8
                  }
                >
                  {isLoading ? (
                    <>
                      <div className="ApplySoloStdFee-loading"></div>
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch className="ApplySoloStdFee-searchBtnIcon" />
                      Search Student
                    </>
                  )}
                </button>

                <button
                  className="ApplySoloStdFee-forgetBtn"
                  onClick={() => navigate("/Forget-Student-ID")}
                >
                  <FaSearch className="ApplySoloStdFee-forgetIcon" />
                  Forgot ID?
                </button>

                <button
                  className="ApplySoloStdFee-clearBtn"
                  onClick={handleClearForm}
                  disabled={isLoading}
                >
                  <FaTimes className="ApplySoloStdFee-clearIcon" />
                  Clear
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Family Student Warning Banner */}
        {isFamilyStudent && familyStudentData && (
          <div className="ApplySoloStdFee-family-warning-banner">
            <FaExclamationTriangle className="ApplySoloStdFee-warning-icon" />
            <div className="ApplySoloStdFee-warning-content">
              <h3>Family Student Detected</h3>
              <p>
                <strong>{familyStudentData.student.name}</strong> belongs to a
                family (Family Status:{" "}
                <strong>{familyStudentData.student.family_status}</strong>).
                Family student fees cannot be applied from this screen.
              </p>
              <p>
                Please use the <strong>Family Fee Management</strong> section.
              </p>
            </div>
            <button
              className="ApplySoloStdFee-family-nav-btn"
              onClick={handleNavigateToFamilyFee}
            >
              <FaArrowRight style={{ marginRight: "8px" }} />
              Go to Family Fee Management
            </button>
          </div>
        )}

        {/* Month Calculation Info */}
        {studentData.admissionDate &&
          studentData.studentId &&
          !isFamilyStudent && (
            <div className="ApplySoloStdFee-monthInfo">
              <FaInfoCircle className="ApplySoloStdFee-monthInfoIcon" />
              <div className="ApplySoloStdFee-monthInfoContent">
                <p>
                  <strong>Admission Date:</strong>{" "}
                  {moment(studentData.admissionDate).format("DD-MM-YYYY")}
                </p>
                <p>
                  <strong>Start Month Set To:</strong>{" "}
                  {moment(startMonth).format("MMMM YYYY")}
                </p>
                {moment(studentData.admissionDate).month() !==
                  moment(startMonth).month() && (
                  <p className="ApplySoloStdFee-monthAdjustmentNote">
                    <FaExclamationTriangle />
                    Note: Fee start month adjusted from admission month (
                    {moment(studentData.admissionDate).format("MMMM YYYY")}) to{" "}
                    {moment(startMonth).format("MMMM YYYY")} as per school
                    policy.
                  </p>
                )}
              </div>
            </div>
          )}

        {/* Update Mode Indicator */}
        {isTotalRecordUpdateMode &&
          studentData.studentId &&
          !isFamilyStudent && (
            <div className="ApplySoloStdFee-updateIndicator">
              <FaExclamationTriangle className="ApplySoloStdFee-updateIcon" />
              <span>
                <strong>Update Mode:</strong> Fee record already exists in the
                system. You can update the existing record.
              </span>
              <div className="ApplySoloStdFee-updateInfo">
                Record ID: {existingTotalRecord?.id} | Created:{" "}
                {moment(existingTotalRecord?.created_at).format("DD-MM-YYYY")}
              </div>
            </div>
          )}

        {/* Student Info Card */}
        {studentData.studentId && !isFamilyStudent && (
          <section className="ApplySoloStdFee-studentSection">
            <div className="ApplySoloStdFee-studentCard">
              <div className="ApplySoloStdFee-studentCardHeader">
                <FaUserGraduate className="ApplySoloStdFee-studentIcon" />
                <h3>Student Information</h3>
                <div className="ApplySoloStdFee-studentBadge">
                  {studentData.studentClass} - {studentData.section}
                </div>
                <button
                  className="ApplySoloStdFee-viewDetailsBtn"
                  onClick={showFeeHistoryModal}
                  style={{ marginLeft: "auto", marginRight: "10px" }}
                  disabled={loadingHistory}
                >
                  {loadingHistory ? (
                    <>
                      <div
                        className="ApplySoloStdFee-loading"
                        style={{
                          border: "2px solid #4a5568",
                          borderTopColor: "transparent",
                        }}
                      ></div>
                      Loading History...
                    </>
                  ) : (
                    <>
                      <FaHistory className="ApplySoloStdFee-viewIcon" />
                      View Fee History
                    </>
                  )}
                </button>
              </div>

              <div className="ApplySoloStdFee-studentInfo">
                <div className="ApplySoloStdFee-studentRow">
                  <div className="ApplySoloStdFee-studentField">
                    <FaUser className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Student Name
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {studentData.studentName}
                      </div>
                    </div>
                  </div>

                  <div className="ApplySoloStdFee-studentField">
                    <FaUserTie className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Father Name
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {studentData.fatherName}
                      </div>
                    </div>
                  </div>

                  <div className="ApplySoloStdFee-studentField">
                    <FaIdCard className="ApplySoloStdFee-fieldIcon" />
                    <div>
                      <div className="ApplySoloStdFee-fieldLabel">
                        Student ID
                      </div>
                      <div className="ApplySoloStdFee-fieldValue">
                        {studentData.studentId}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-studentActions">
                  <button
                    className="ApplySoloStdFee-viewDetailsBtn"
                    onClick={showStudentDetailsModal}
                  >
                    <FaEye className="ApplySoloStdFee-viewIcon" />
                    View Complete Details
                  </button>

                  <div className="ApplySoloStdFee-studentQuickStats">
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Monthly Fee</span>
                      <strong>{formatCurrency(studentData.monthlyFee)}</strong>
                    </div>
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Discount</span>
                      <strong>{studentData.discountPercentage}%</strong>
                    </div>
                    <div className="ApplySoloStdFee-quickStat">
                      <span>Status</span>
                      <strong
                        className={
                          lastPending > 0
                            ? "ApplySoloStdFee-status-pending"
                            : "ApplySoloStdFee-status-paid"
                        }
                      >
                        {lastPending > 0 ? "Pending" : "Paid"}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Fee Application Form */}
        {studentData.studentId && !isFamilyStudent && (
          <section className="ApplySoloStdFee-formSection">
            <div className="ApplySoloStdFee-formCard">
              <div className="ApplySoloStdFee-formHeader">
                <FaMoneyBillWave className="ApplySoloStdFee-formIcon" />
                <h2>
                  {isTotalRecordUpdateMode
                    ? "Update Fee Record"
                    : "Submit New Fee Record"}
                </h2>
                <div className="ApplySoloStdFee-formModeBadge">
                  {isTotalRecordUpdateMode ? (
                    <>
                      <FaEdit className="ApplySoloStdFee-modeIcon" />
                      Update Mode
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="ApplySoloStdFee-modeIcon" />
                      Submit Mode
                    </>
                  )}
                </div>
                <button
                  className="ApplySoloStdFee-clearFormBtn"
                  onClick={handleClearForm}
                >
                  <FaTimes className="ApplySoloStdFee-clearIcon" />
                  Clear Form
                </button>
                <button
                  className="ApplySoloStdFee-debugInfoBtn"
                  onClick={showDebugPanel}
                >
                  <FaBug className="ApplySoloStdFee-debugIcon" />
                  Debug Info
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {/* One-Time Charges */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCreditCard className="ApplySoloStdFee-subtitleIcon" />
                    One-Time Charges
                  </h3>

                  <div className="ApplySoloStdFee-formGrid">
                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Annual Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={studentData.annualCharges}
                        onChange={(e) =>
                          setStudentData((prev) => ({
                            ...prev,
                            annualCharges: parseFloat(e.target.value) || 0,
                          }))
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Admission Fee
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={studentData.admissionFee}
                        onChange={(e) =>
                          setStudentData((prev) => ({
                            ...prev,
                            admissionFee: parseFloat(e.target.value) || 0,
                          }))
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Workbook Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={studentData.workbookCharges}
                        onChange={(e) =>
                          setStudentData((prev) => ({
                            ...prev,
                            workbookCharges: parseFloat(e.target.value) || 0,
                          }))
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Books Charges
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={studentData.booksCharges}
                        onChange={(e) =>
                          setStudentData((prev) => ({
                            ...prev,
                            booksCharges: parseFloat(e.target.value) || 0,
                          }))
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Monthly Fee Period */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCalendarAlt className="ApplySoloStdFee-subtitleIcon" />
                    Monthly Fee Period
                  </h3>

                  <div className="ApplySoloStdFee-formRow">
                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaMoneyBillWave className="ApplySoloStdFee-labelIcon" />
                        Monthly Fee
                      </label>
                      <input
                        type="number"
                        className="ApplySoloStdFee-formInput"
                        value={studentData.monthlyFee}
                        onChange={(e) =>
                          setStudentData((prev) => ({
                            ...prev,
                            monthlyFee: parseFloat(e.target.value) || 0,
                          }))
                        }
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalendarAlt className="ApplySoloStdFee-labelIcon" />
                        Start Month
                      </label>
                      <DatePicker
                        selected={startMonth}
                        onChange={handleStartMonthChange}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        className="ApplySoloStdFee-datePicker"
                        minDate={
                          studentData.admissionDate
                            ? new Date(studentData.admissionDate)
                            : new Date("2000-01-01")
                        }
                        maxDate={
                          new Date(
                            new Date().setFullYear(new Date().getFullYear() + 2)
                          )
                        }
                        popperClassName="ApplySoloStdFee-datePicker-popper"
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalendarAlt className="ApplySoloStdFee-labelIcon" />
                        End Month
                      </label>
                      <DatePicker
                        selected={endMonth}
                        onChange={handleEndMonthChange}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        className="ApplySoloStdFee-datePicker"
                        minDate={startMonth}
                        maxDate={
                          new Date(
                            new Date(startMonth).setFullYear(
                              new Date(startMonth).getFullYear() + 2
                            )
                          )
                        }
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalculator className="ApplySoloStdFee-labelIcon" />
                        Total Months
                      </label>
                      <input
                        type="text"
                        className="ApplySoloStdFee-formInput"
                        value={`${monthCount} months`}
                        readOnly
                      />
                    </div>

                    <div className="ApplySoloStdFee-formGroup">
                      <label className="ApplySoloStdFee-formLabel">
                        <FaCalculator className="ApplySoloStdFee-labelIcon" />
                        Total Fee
                      </label>
                      <input
                        type="text"
                        className="ApplySoloStdFee-formInput ApplySoloStdFee-totalFeeInput"
                        value={formatCurrency(totalFee)}
                        readOnly
                      />
                    </div>
                  </div>

                  {/* Month Range Display */}
                  {monthCount > 0 && (
                    <div className="ApplySoloStdFee-monthRangeDisplay">
                      <FaInfoCircle className="ApplySoloStdFee-monthRangeIcon" />
                      <span>
                        Selected Period: {getMonthRangeInfo()} | Total: {monthCount} months × {formatCurrency(studentData.monthlyFee || 0)} = {formatCurrency(totalFee)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Payment Summary */}
                <div className="ApplySoloStdFee-formSectionGroup">
                  <h3 className="ApplySoloStdFee-formSubtitle">
                    <FaCalculator className="ApplySoloStdFee-subtitleIcon" />
                    Payment Summary
                  </h3>

                  <div className="ApplySoloStdFee-paymentGrid">
                    <div className="ApplySoloStdFee-paymentCard">
                      <div className="ApplySoloStdFee-paymentHeader">
                        <FaMoneyBillWave className="ApplySoloStdFee-paymentIcon" />
                        <h4>Fee Breakdown</h4>
                      </div>

                      <div className="ApplySoloStdFee-paymentDetails">
                        <div className="ApplySoloStdFee-paymentRow">
                          <span>One-Time Charges</span>
                          <strong>
                            {formatCurrency(summary.oneTimeCharges)}
                          </strong>
                        </div>

                        <div className="ApplySoloStdFee-paymentRow">
                          <span>Monthly Fee ({monthCount} months)</span>
                          <strong>{formatCurrency(totalFee)}</strong>
                        </div>

                        <div className="ApplySoloStdFee-paymentRow ApplySoloStdFee-paymentTotal">
                          <span>Total Payable</span>
                          <strong>
                            {formatCurrency(summary.totalPayable)}
                          </strong>
                        </div>
                      </div>
                    </div>

                    <div className="ApplySoloStdFee-paymentCard">
                      <div className="ApplySoloStdFee-paymentHeader">
                        <FaPercent className="ApplySoloStdFee-paymentIcon" />
                        <h4>Discount & Payment</h4>
                      </div>

                      <div className="ApplySoloStdFee-paymentForm">
                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Discount (%)
                          </label>
                          <input
                            type="number"
                            className="ApplySoloStdFee-formInput"
                            value={studentData.discount}
                            onChange={(e) =>
                              handleDiscountChange(e.target.value)
                            }
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="0-100%"
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Discount Amount
                          </label>
                          <input
                            type="text"
                            className="ApplySoloStdFee-formInput"
                            value={formatCurrency(summary.discountAmount)}
                            readOnly
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Final Total
                          </label>
                          <input
                            type="text"
                            className="ApplySoloStdFee-formInput ApplySoloStdFee-finalTotal"
                            value={formatCurrency(finalTotal)}
                            readOnly
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Amount Paid
                          </label>
                          <input
                            type="number"
                            className="ApplySoloStdFee-formInput"
                            value={totalPaid}
                            onChange={(e) =>
                              handleTotalPaidChange(e.target.value)
                            }
                            min="0"
                            step="0.01"
                            placeholder="Enter amount"
                          />
                        </div>

                        <div className="ApplySoloStdFee-formGroup">
                          <label className="ApplySoloStdFee-formLabel">
                            Balance Amount
                          </label>
                          <input
                            type="text"
                            className={`ApplySoloStdFee-formInput ${
                              lastPending > 0
                                ? "ApplySoloStdFee-pending"
                                : "ApplySoloStdFee-paid"
                            }`}
                            value={formatCurrency(lastPending)}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="ApplySoloStdFee-formActions">
                  <button
                    type="button"
                    className="ApplySoloStdFee-printBtn"
                    onClick={handlePrintReceipt}
                    disabled={!totalPaid || isSubmitting}
                  >
                    <FaFileInvoiceDollar className="ApplySoloStdFee-printIcon" />
                    Print Receipt
                  </button>

                  <button
                    type="submit"
                    className={`ApplySoloStdFee-submitBtn ${
                      isTotalRecordUpdateMode ? "ApplySoloStdFee-updateBtn" : ""
                    } ${isSubmitting ? "ApplySoloStdFee-submitting" : ""}`}
                    disabled={!totalPaid || lastPending < 0 || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="ApplySoloStdFee-loading-small"></div>
                        Processing...
                      </>
                    ) : isTotalRecordUpdateMode ? (
                      <>
                        <FaEdit className="ApplySoloStdFee-submitIcon" />
                        Update Fee Record
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="ApplySoloStdFee-submitIcon" />
                        Submit New Record
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {/* Quick Summary */}
        {studentData.studentId && !isFamilyStudent && (
          <section className="ApplySoloStdFee-summarySection">
            <div className="ApplySoloStdFee-summaryCard">
              <div className="ApplySoloStdFee-summaryHeader">
                <FaChartLine className="ApplySoloStdFee-summaryIcon" />
                <h3 style={{color:'white'}}>Fee Summary</h3>
                <div className="ApplySoloStdFee-summaryStatus">
                  {isTotalRecordUpdateMode ? "UPDATE MODE" : "NEW RECORD"}
                </div>
              </div>

              <div className="ApplySoloStdFee-summaryGrid">
                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaMoneyBillWave />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Total Payable
                    </div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {formatCurrency(summary.totalPayable)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaPercent />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">Discount</div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {summary.discount}% (
                      {formatCurrency(summary.discountAmount)})
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaCalculator />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Final Total
                    </div>
                    <div className="ApplySoloStdFee-summaryValue ApplySoloStdFee-summaryTotal">
                      {formatCurrency(finalTotal)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaCreditCard />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">
                      Amount Paid
                    </div>
                    <div className="ApplySoloStdFee-summaryValue ApplySoloStdFee-summaryPaid">
                      {formatCurrency(summary.paid)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaHistory />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">Balance</div>
                    <div
                      className={`ApplySoloStdFee-summaryValue ${
                        summary.balance > 0
                          ? "ApplySoloStdFee-summaryPending"
                          : "ApplySoloStdFee-summaryCleared"
                      }`}
                    >
                      {formatCurrency(summary.balance)}
                    </div>
                  </div>
                </div>

                <div className="ApplySoloStdFee-summaryItem">
                  <div className="ApplySoloStdFee-summaryIconWrapper">
                    <FaCalendarAlt />
                  </div>
                  <div className="ApplySoloStdFee-summaryContent">
                    <div className="ApplySoloStdFee-summaryLabel">Period</div>
                    <div className="ApplySoloStdFee-summaryValue">
                      {monthCount} months
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Family Student Warning Content */}
        {isFamilyStudent && (
          <div className="ApplySoloStdFee-family-content">
            <div className="ApplySoloStdFee-family-warning-card">
              <div className="ApplySoloStdFee-family-warning-header">
                <FaBan className="ApplySoloStdFee-family-ban-icon" />
                <h3>Access Restricted</h3>
              </div>
              <div className="ApplySoloStdFee-family-warning-body">
                <p>
                  <strong>Student:</strong> {familyStudentData?.student?.name}
                </p>
                <p>
                  <strong>Family Status:</strong>{" "}
                  {familyStudentData?.student?.family_status}
                </p>
                {familyStudentData?.family_code && (
                  <p>
                    <strong>Family Code:</strong>{" "}
                    {familyStudentData.family_code}
                  </p>
                )}
                <div className="ApplySoloStdFee-family-instruction">
                  <FaExclamationTriangle />
                  <span>
                    This student belongs to a family. Family student fees cannot
                    be applied from this screen.
                  </span>
                </div>
                <div className="ApplySoloStdFee-family-solution">
                  <FaUsers />
                  <span>
                    Please use the <strong>Family Fee Management</strong>{" "}
                    section to manage fees for this student.
                  </span>
                </div>
              </div>
              <div className="ApplySoloStdFee-family-actions">
                <button
                  className="ApplySoloStdFee-family-action-btn primary"
                  onClick={handleNavigateToFamilyFee}
                >
                  <FaArrowRight />
                  Go to Family Fee Management
                </button>
                <button
                  className="ApplySoloStdFee-family-action-btn secondary"
                  onClick={() => {
                    setIsFamilyStudent(false);
                    setFamilyStudentData(null);
                    setStudentIdPart2("");
                    setStudentIdPart3("");
                  }}
                >
                  <FaSearch />
                  Search Another Student
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {studentData.studentId && !isFamilyStudent && (
          <footer className="ApplySoloStdFee-footer">
            <div className="ApplySoloStdFee-footerContent">
              <div className="ApplySoloStdFee-footerLeft">
                <div className="ApplySoloStdFee-footerLogo">
                  <FaMoneyBillWave className="ApplySoloStdFee-footerIcon" />
                  Fee Management System
                </div>
                <p className="ApplySoloStdFee-footerText">
                  Processing fees for <strong>{studentData.studentName}</strong>{" "}
                  | Class: {studentData.studentClass} | Period:{" "}
                  {moment(startMonth).format("MMM YYYY")} -{" "}
                  {moment(endMonth).format("MMM YYYY")} | Mode:{" "}
                  {isTotalRecordUpdateMode ? "Update" : "New"}
                </p>
              </div>

              <div className="ApplySoloStdFee-footerRight">
                <div className="ApplySoloStdFee-systemStatus">
                  <div
                    className={`ApplySoloStdFee-statusDot ${
                      systemStatus === "ready"
                        ? "ApplySoloStdFee-statusReady"
                        : systemStatus === "updating" ||
                          systemStatus === "submitting"
                        ? "ApplySoloStdFee-statusProcessing"
                        : "ApplySoloStdFee-statusWarning"
                    }`}
                  ></div>
                  {systemStatus.replace("_", " ").toUpperCase()}
                </div>
                <div className="ApplySoloStdFee-lastUpdated">
                  Last updated: {moment().format("DD MMM YYYY, hh:mm A")}
                </div>
              </div>
            </div>
          </footer>
        )}

        {/* Empty State */}
        {!studentData.studentId && !isFamilyStudent && (
          <section className="ApplySoloStdFee-emptyState">
            <div className="ApplySoloStdFee-emptyStateCard">
              <div className="ApplySoloStdFee-emptyStateIcon">
                <FaUserGraduate />
              </div>
              <h3>Search for a Student</h3>
              <p>
                Enter a Student ID above to apply fees and expenses for a
                student.
              </p>
              <div className="ApplySoloStdFee-emptyStateTips">
                <FaInfoCircle className="ApplySoloStdFee-tipsIcon" />
                <div>
                  <p>
                    <strong>Student ID Format:</strong> Std-XXX-XXXXXXXX
                  </p>
                  <p>
                    <strong>Examples:</strong> Std-PG-260001, Std-PG-2650001
                  </p>
                  <p>
                    <strong>Note:</strong> Numeric part must be 4-8 digits
                  </p>
                  <p>
                    <strong>Family Students:</strong> Will be redirected to
                    Family Fee Management
                  </p>
                  <p>
                    <strong>Start Month Logic:</strong> Automatically set based
                    on admission date or current month
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* CSS for family student warning and month info */}
      <style>
        {`
          /* Family Student Modal Styles */
          .family-student-popup .swal2-popup {
            border: 3px solid #e74c3c;
            border-radius: 12px;
          }

          .family-student-modal {
            text-align: center;
            padding: 20px;
          }

          .family-student-warning {
            margin-bottom: 20px;
          }

          .family-student-warning h3 {
            color: #e74c3c;
            margin: 10px 0;
          }

          .family-student-message {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 8px;
            margin: 20px 0;
            text-align: left;
          }

          .family-student-message p {
            margin: 8px 0;
            font-size: 16px;
          }

          .family-student-instructions {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            padding: 15px;
            border-radius: 8px;
            margin: 20px 0;
            text-align: left;
          }

          .family-student-instructions p {
            margin: 8px 0;
            color: #856404;
          }

          .family-student-actions {
            margin-top: 25px;
            padding-top: 20px;
            border-top: 1px solid #eee;
          }

          .nav-to-family-btn {
            background: #3498db;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            font-size: 16px;
            cursor: pointer;
            transition: background 0.3s;
            width: 100%;
          }

          .nav-to-family-btn:hover {
            background: #2980b9;
          }

          /* Family Student Warning Banner */
          .ApplySoloStdFee-family-warning-banner {
            background: linear-gradient(135deg, #ffeaa7, #fab1a0);
            border: 2px solid #e74c3c;
            border-radius: 10px;
            padding: 20px;
            margin: 20px 2rem;
            display: flex;
            align-items: center;
            gap: 20px;
            box-shadow: 0 4px 12px rgba(231, 76, 60, 0.15);
          }

          .ApplySoloStdFee-warning-icon {
            font-size: 2.5rem;
            color: #e74c3c;
            flex-shrink: 0;
          }

          .ApplySoloStdFee-warning-content {
            flex: 1;
            text-align: left;
          }

          .ApplySoloStdFee-warning-content h3 {
            color: #e74c3c;
            margin-bottom: 8px;
          }

          .ApplySoloStdFee-warning-content p {
            margin: 5px 0;
            color: #2d3436;
          }

          .ApplySoloStdFee-family-nav-btn {
            background: #e74c3c;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 6px;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.3s;
            white-space: nowrap;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .ApplySoloStdFee-family-nav-btn:hover {
            background: #c0392b;
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(231, 76, 60, 0.3);
          }

          /* Family Student Warning Card */
          .ApplySoloStdFee-family-content {
            margin-top: 2rem;
          }

          .ApplySoloStdFee-family-warning-card {
            background: white;
            border: 2px solid #e74c3c;
            border-radius: 12px;
            padding: 2rem;
            max-width: 800px;
            margin: 0 auto;
            box-shadow: 0 8px 25px rgba(231, 76, 60, 0.1);
          }

          .ApplySoloStdFee-family-warning-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #ffeaa7;
          }

          .ApplySoloStdFee-family-ban-icon {
            font-size: 2.5rem;
            color: #e74c3c;
          }

          .ApplySoloStdFee-family-warning-header h3 {
            color: #e74c3c;
            margin: 0;
            font-size: 1.5rem;
          }

          .ApplySoloStdFee-family-warning-body {
            margin-bottom: 2rem;
          }

          .ApplySoloStdFee-family-warning-body p {
            margin: 0.75rem 0;
            font-size: 1.1rem;
            color: #2c3e50;
          }

          .ApplySoloStdFee-family-instruction,
          .ApplySoloStdFee-family-solution {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            margin: 1.5rem 0;
            padding: 1rem;
            border-radius: 8px;
          }

          .ApplySoloStdFee-family-instruction {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
          }

          .ApplySoloStdFee-family-instruction svg {
            color: #f39c12;
            font-size: 1.5rem;
            margin-top: 0.25rem;
          }

          .ApplySoloStdFee-family-solution {
            background: #e8f5e9;
            border: 1px solid #c8e6c9;
          }

          .ApplySoloStdFee-family-solution svg {
            color: #27ae60;
            font-size: 1.5rem;
            margin-top: 0.25rem;
          }

          .ApplySoloStdFee-family-instruction span,
          .ApplySoloStdFee-family-solution span {
            flex: 1;
            color: #2d3436;
            font-size: 1rem;
            line-height: 1.5;
          }

          .ApplySoloStdFee-family-actions {
            display: flex;
            gap: 1rem;
            margin-top: 2rem;
          }

          .ApplySoloStdFee-family-action-btn {
            flex: 1;
            padding: 1rem 1.5rem;
            border: none;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
          }

          .ApplySoloStdFee-family-action-btn.primary {
            background: #3498db;
            color: white;
          }

          .ApplySoloStdFee-family-action-btn.primary:hover {
            background: #2980b9;
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
          }

          .ApplySoloStdFee-family-action-btn.secondary {
            background: #f8f9fa;
            color: #2c3e50;
            border: 2px solid #e0e0e0;
          }

          .ApplySoloStdFee-family-action-btn.secondary:hover {
            background: #e9ecef;
            border-color: #3498db;
            color: #3498db;
          }

          /* Month Info Styles */
          .ApplySoloStdFee-monthInfo {
            background: #e8f4fd;
            border: 1px solid #b6d8f5;
            border-radius: 8px;
            padding: 12px 15px;
            margin: 15px 2rem;
            display: flex;
            align-items: flex-start;
            gap: 12px;
          }

          .ApplySoloStdFee-monthInfoIcon {
            color: #3498db;
            font-size: 18px;
            margin-top: 2px;
          }

          .ApplySoloStdFee-monthInfoContent {
            flex: 1;
          }

          .ApplySoloStdFee-monthInfoContent p {
            margin: 4px 0;
            font-size: 14px;
            color: #2c3e50;
          }

          .ApplySoloStdFee-monthAdjustmentNote {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            padding: 8px 12px;
            border-radius: 4px;
            margin-top: 8px !important;
            color: #856404 !important;
            font-size: 13px !important;
            display: flex;
            align-items: flex-start;
            gap: 6px;
          }

          .ApplySoloStdFee-monthAdjustmentNote svg {
            color: #f39c12;
            margin-top: 2px;
          }

          /* Month Range Display */
          .ApplySoloStdFee-monthRangeDisplay {
            background: #e8f5e9;
            border: 1px solid #c8e6c9;
            border-radius: 6px;
            padding: 10px 15px;
            margin-top: 15px;
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 14px;
            color: #2e7d32;
          }

          .ApplySoloStdFee-monthRangeIcon {
            color: #27ae60;
            font-size: 16px;
          }

          /* Total Fee Input */
          .ApplySoloStdFee-totalFeeInput {
            background-color: #f8f9fa !important;
            font-weight: bold !important;
            color: #2c3e50 !important;
            border: 2px solid #3498db !important;
          }

          .ApplySoloStdFee-datePicker-popper {
            z-index: 9999 !important;
          }

          @media (max-width: 768px) {
            .ApplySoloStdFee-family-warning-banner {
              flex-direction: column;
              text-align: center;
              margin: 20px 1rem;
              padding: 1.5rem;
            }

            .ApplySoloStdFee-warning-content {
              text-align: center;
            }

            .ApplySoloStdFee-family-actions {
              flex-direction: column;
            }

            .ApplySoloStdFee-monthInfo {
              margin: 15px 1rem;
            }

            .ApplySoloStdFee-monthRangeDisplay {
              flex-direction: column;
              align-items: flex-start;
              gap: 5px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ApplySoloStdFee;